import React, { Fragment, useEffect } from 'react';
// Internal imports
import documentForm from '~/images/icons/icons8-google-form.png';
import attachIcon from '~/images/icons/icons8-attach.png';
import instructionsIcon from '~/images/icons/icons8-user_manual.png';
import attachIconWhite from '~/images/icons/icons8-attach-2x.png';
import AddDocumentModal from '../ArchivedDocuments/Modals/AddDocumentModal.jsx';
import WebHelpers, { statusOK } from '~/app/webHelpers.js';
import XlinkAPI from '~/app/api/xlinkAPI';
import { useSetState } from '~/app/Utility/customHooks';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';
import {
  buildPreviewString,
  convertTimestampToLocal,
} from '~/app/Pages/Returns/components/Document/ArchivedDocuments/archivedDocumentsHelpers';
import { formatMaskSSNEIN } from '~/app/Utility/general.js';
// External imports
import { useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  Input,
  NativeSelect,
  FormControl,
  FormHelperText,
  withStyles,
  FormLabel,
  Button,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
// Styling imports
import '~/app/Pages/Returns/components/Document/AttachDocumentModal/css/AttachDocumentModal.css';
import { styles } from '~/app/Pages/Returns/components/Document/AttachDocumentModal/css/AttachDocumentModal.js';

const instructions = `To add a new document, click > Add New Documents. Next,
  click > Browse > double click on your document > enter a
  document description > choose a type > click Add. Lastly,
  select a document from the list below > enter an attachment
  description > click Attach.`;

/**
 * This component renders the AttachDocumentModal that lets the user attach documents to a link in "Binary Attachments" within FormViewer
 *
 * @component AttachDocumentModal
 * @category FormViewer
 **/
const AttachDocumentModal = props => {
  const [state, setState] = useSetState({
    userDescription: '',
    documentID: 0,
    image: null,
    imageName: '',
    imageDescription: '',
    imageType: 1,
    importDocumentModalOpen: false,
    attachmentType: '',
    selectedAddedDocument: -1,
    isAddDocumentModalOpen: false,
    selectedBinaryAttachment: props.documentsToBeAttached?.[0],
    binaryAttachments: props.documentsToBeAttached,
    buttonID: props.buttonID,
    disableDescription: false,
  });

  const { classes } = props;
  const dispatch = useDispatch();
  const maskSSNEIN = useSelector(state => state.defaultsPageState.maskSSNEIN);

  useEffect(() => {
    // some binary attachments have a fixed value for the description
    // if it exists, set fixed description and disable description input box
    if (state.selectedBinaryAttachment?.fixed !== '') {
      setState({
        userDescription: state.selectedBinaryAttachment?.fixed,
        disableDescription: true,
      });
    }
  }, [state.selectedBinaryAttachment]);

  const handleDocumentSelection = doc => {
    setState({
      documentID: doc.document_id,
      selectedAddedDocument: doc.document_id,
      imageName: doc.document_name,
      imageDescription: doc.document_description,
      attachmentType: doc.document_media_type,
    });
  };

  const handleBinaryAttachmentSelection = e => {
    const binaryAttachment = state.binaryAttachments.find(
      attachment => attachment.displayName === e.target.value,
    );

    setState({
      selectedBinaryAttachment: binaryAttachment,
    });
  };

  const getAddedDocumentList = () => {
    const eligibleDocs = (props.attachments || []).filter(
      doc => doc.document_media_type?.includes('/pdf;') && !doc.document_attachment_id,
    );

    if (eligibleDocs.length === 0) {
      return (
        <Typography classes={{ root: classes.emptyStateText }}>
          No documents available. Use &quot;Add New Documents&quot; to upload a PDF.
        </Typography>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            {['SSN', 'NAME', 'DOCUMENT DESCRIPTION', 'DOCUMENT TYPE', 'TIME'].map(col => (
              <TableCell
                key={col}
                className={col === 'SSN' ? classes.attachListSSNHeader : undefined}
                classes={{ root: classes.attachListCellHeader }}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {eligibleDocs.map(doc => {
            const isSelected = state.selectedAddedDocument === doc.document_id;
            return (
              <TableRow
                key={doc.document_id}
                hover
                selected={isSelected}
                onClick={() => handleDocumentSelection(doc)}
                classes={{ root: classes.attachListRow, selected: classes.attachListRowSelected }}
              >
                <TableCell classes={{ root: classes.attachListCellRoot }}>
                  {formatMaskSSNEIN(
                    doc.ssnein ?? '',
                    maskSSNEIN,
                    WebHelpers.getJWTPayload()?.is_business,
                  )}
                </TableCell>
                <TableCell classes={{ root: classes.attachListCellRoot }}>
                  {doc.client_name || ''}
                </TableCell>
                <TableCell classes={{ root: classes.attachListCellRoot }}>
                  {buildPreviewString(doc.document_description)}
                </TableCell>
                <TableCell classes={{ root: classes.attachListCellRoot }}>
                  {doc.document_type}
                </TableCell>
                <TableCell classes={{ root: classes.attachListCellRoot }}>
                  {convertTimestampToLocal(doc.create_date)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  /**
   * For passing document state up to parent FormViewer where addOrAttachDocument makes the req to upload document
   *
   * @param {event} e used as domEvent in async req addOrAttachDocument
   * @returns {undefined} blank return
   */
  const handleDocumentUpload = async e => {
    if (state.userDescription === '' || state.selectedAddedDocument === -1) {
      props.showSnackBarErr(
        'An attachment description must be entered and a document must be selected',
      );
      return;
    } // No Document Selected or Attach description
    let image = '';
    try {
      const res = await XlinkAPI.getReturnAttachmentByRIDDocId(0, state.documentID);
      if (statusOK(res)) {
        image = res.data.split(',')[1];
      }
    } catch (err) {
      dispatch(
        formViewerActions.showErrorDialog(
          'Unable to attach the document to the return.',
          'Attach Document Error',
        ),
      );
    }

    await props.addOrAttachDocument(
      e,
      state.buttonID,
      state.selectedBinaryAttachment?.efcode,
      state.userDescription.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
      image,
      state.imageDescription,
      state.imageName,
      state.imageType,
      state.attachmentType,
      state.selectedAddedDocument,
      state.selectedBinaryAttachment,
      props.dataBin,
      props.dataBinSig,
    );
    props.handleCloseAttachDocumentModal();
  };

  /**
   * For handling change to description input
   *
   * @param {event} e for sending target value of user description
   */
  const handleDescriptionChange = e => {
    setState({ userDescription: e.target.value });
  };

  /** Handles opening/closing the add document modal */
  const toggleAddDocumentModal = () => {
    setState({ isAddDocumentModalOpen: !state.isAddDocumentModalOpen });
  };

  /**
   * renders form section displaying form type selected, and renders description input
   *
   * @returns {jsx} renders UI and Input
   */
  const renderToFormSection = () => {
    return (
      <Fragment>
        <div className="render-form-section-div-style">
          <FormControl classes={{ root: classes.styledFormControl }}>
            <NativeSelect
              disableUnderline
              classes={{ root: classes.docUploadInp }}
              value={state.selectedBinaryAttachment?.displayName}
              onChange={handleBinaryAttachmentSelection}
            >
              <option value={-1} disabled>
                Select Binary Attachment
              </option>
              {state.binaryAttachments?.map((attachment, i) => {
                return (
                  <option key={i} value={attachment.displayName}>
                    {attachment.displayName}
                  </option>
                );
              })}
            </NativeSelect>
          </FormControl>
          <div className="attach-doc-secttion-image-container">
            <img
              className="render-form-section-image-style"
              id="clientNameInfo"
              src={documentForm}
            />
          </div>
          <div className="render-form-section-div-two">
            <FormLabel classes={{ root: classes.styledFormLabelOne }}>
              {state.selectedBinaryAttachment?.displayName}
            </FormLabel>
            <FormLabel classes={{ root: classes.styledFormLabelTwo }}>
              {state.selectedBinaryAttachment?.hint}
            </FormLabel>
          </div>
        </div>
        <Grid container>
          <Grid item xs={12} className={classes.styledFlexGrid}>
            <Grid container justify="flex-start">
              <FormLabel classes={{ root: classes.styledFormLabelThree }}>
                Attachment Description{' '}
              </FormLabel>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.styledFlexGrid}>
            <Grid container justify="center">
              <Input
                id="userDescription"
                disableUnderline
                value={state.userDescription}
                onChange={handleDescriptionChange}
                classes={{ root: classes.styledInput }}
                disabled={state.disableDescription}
              />
            </Grid>
          </Grid>
        </Grid>
      </Fragment>
    );
  };

  return (
    <div>
      <AddDocumentModal
        handleCloseDocumentModalOk={props.addOrAttachDocument}
        showSnackBarErr={props.showSnackBarErr}
        isAddDocumentModalOpen={state.isAddDocumentModalOpen}
        toggleAddDocumentModal={toggleAddDocumentModal}
      />
      <Typography
        variant="body2"
        id="simple-modal-title"
        classes={{ root: classes.styledTypography }}
      >
        <img id="paperClipIcon" className="attach-doc-img-one" src={attachIconWhite} />
        <span className="attach-doc-header">Attach Document</span>
      </Typography>
      <div className="attach-doc-div-one">
        <div className="attach-doc-div-two">
          <Grid container>
            <Grid item xs={9} classes={{ item: classes.instructionsContainer }}>
              <div className="attach-doc-instructions-img-container">
                <img
                  id="instructionsIcon"
                  className="attach-doc-instructions-img"
                  src={instructionsIcon}
                />
              </div>
              <div className="attach-doc-instructions-text-container">
                <Typography classes={{ root: classes.instructionsTitle }} variant="h6" gutterBottom>
                  Instructions:
                </Typography>
                <Typography classes={{ root: classes.instructions }} variant="body1">
                  {instructions}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={9} className={classes.styledFlexGrid}>
              <Typography classes={{ root: classes.docTitle }} variant="h6" gutterBottom>
                Attach Document
              </Typography>
            </Grid>
            <Grid item xs={3} className={classes.styledFlexGrid}>
              <Grid container justify="flex-end">
                <Button
                  id="btnOpenAttachModalDocArchive"
                  size="medium"
                  color="primary"
                  classes={{ root: classes.addDocumentBtn }}
                  onClick={toggleAddDocumentModal}
                >
                  Add New Documents
                </Button>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="column"
              xs={12}
              className={classes.styledGridWithPadding}
            >
              <div className={classes.attachListContainer}>{getAddedDocumentList()}</div>
              <FormHelperText>IRS only allows attachments of PDF file</FormHelperText>
            </Grid>
            <Grid
              item
              xs={5}
              classes={{
                item: classes.styledFlexGridDivider,
              }}
            >
              <Grid container justify="flex-start">
                <Divider
                  classes={{
                    root: classes.documentDivider,
                  }}
                  variant="fullWidth"
                  light={true}
                />
              </Grid>
            </Grid>
            <Grid item xs={2} classes={{ item: classes.styledFlexGridImage }}>
              <Grid container justify="center">
                <img id="clientNameInfo" className="noselect" src={attachIcon} />
              </Grid>
            </Grid>
            <Grid
              item
              xs={5}
              classes={{
                item: classes.styledFlexGridDivider,
              }}
            >
              <Divider
                classes={{ root: classes.documentDivider }}
                variant="fullWidth"
                light={true}
              />
            </Grid>
            <Grid item xs={12}>
              <div className="attach-doc-div-five">
                <Typography variant="h6" gutterBottom classes={{ root: classes.docTitle }}>
                  To Form
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12}>
              {state.buttonID != null && renderToFormSection()}
            </Grid>
            <Grid item xs={12} classes={{ item: classes.styledGridWithMargin }}>
              <Grid container justify="flex-end">
                <div className="attach-doc-div-four">
                  <Button
                    classes={{ root: classes.styleButtonMarginRight }}
                    size="small"
                    color="default"
                    onClick={props.handleCloseAttachDocumentModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    id="btnAttachAddDocModal"
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={e => handleDocumentUpload(e)}
                  >
                    Attach
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(AttachDocumentModal);
